import { useEffect, useRef, useState } from 'react';
import { Modal, Form, Select, Alert, Tag, Spinner, Button, message } from '../../ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { request } from '../../api/client';
import { useDebounce } from '../../hooks/useDebounce';
import { customerOption } from '../../components/CustomerOption';

interface Props {
  open: boolean;
  onClose: () => void;
  customerType: 'RETAILER' | 'FARMER';
  /** The record being merged AWAY (the duplicate). */
  duplicate: any;
}

/**
 * Merge this (duplicate) retailer/farmer into a surviving record: every reference (orders,
 * invoices, payments, ledger, visits, complaints…) re-points to the survivor, the duplicate's
 * outstanding folds in (retailers), and the duplicate is closed + soft-deleted.
 */
export function MergeCustomerModal({ open, onClose, customerType, duplicate }: Props) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const term = useDebounce(search, 350);
  const isRetailer = customerType === 'RETAILER';
  const dupName = duplicate?.companyName ?? duplicate?.fullName ?? 'this record';

  const { data, isFetching } = useQuery({
    queryKey: ['merge-survivor-search', customerType, term],
    queryFn: () => request<any>({ url: isRetailer ? '/retailers' : '/farmers', params: { search: term, pageSize: 20 } }),
    enabled: open && term.trim().length >= 2,
  });
  const found = (data?.data ?? [])
    .filter((r: any) => String(r.id) !== String(duplicate?.id))
    .map((r: any) => customerOption(r, isRetailer ? 'RETAILER' : 'FARMER', { showMobile: true }));
  // The picked survivor keeps its label once the search box clears (closing the dropdown resets the search).
  const survivorId = Form.useWatch('survivorId', form);
  const seen = useRef(new Map<string, ReturnType<typeof customerOption>>());
  for (const o of found) seen.current.set(o.value, o);
  const picked = survivorId ? seen.current.get(String(survivorId)) : undefined;
  const options = picked && !found.some((o: any) => o.value === picked.value) ? [picked, ...found] : found;

  useEffect(() => { if (open) { form.resetFields(); setSearch(''); } }, [open, form]);

  const merge = useConfirmedMutation({ title: "Merge this duplicate into the surviving customer?", content: "Orders, payments and history move across, and the duplicate is retired. This can’t be undone.", okText: "Yes, merge", danger: true }, {
    mutationFn: (v: any) => request({ url: '/territory-assignments/merge-customer', method: 'POST', data: { customerType, duplicateId: String(duplicate.id), survivorId: v.survivorId } }),
    onSuccess: (res: any) => {
      const tables = Object.keys(res?.repointed ?? {}).length;
      message.success(`Merged — references across ${tables} table(s) moved to the survivor`);
      qc.invalidateQueries({ queryKey: ['retailers'] });
      qc.invalidateQueries({ queryKey: ['farmers'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to merge'),
  });

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title={`Merge duplicate · ${dupName}`}
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="danger" loading={merge.isPending} onClick={() => form.submit()}>Merge</Button>
      </>}>
      {duplicate && (
        <Form form={form} layout="vertical" onFinish={(v) => merge.mutate(v)}>
          <div style={{ marginBottom: 12 }}>
            Merge <Tag tone="red">{dupName}</Tag> into the survivor below. All of its orders, invoices, payments and history move to the survivor; this record is then closed.
          </div>
          <Form.Item name="survivorId" label="Survivor (the record to keep)" rules={[{ required: true, message: 'Pick the surviving record' }]}>
            <Select
              showSearch filterOption={false} onSearch={setSearch}
              placeholder={`Search ${isRetailer ? 'retailers' : 'farmers'} by name / mobile`}
              notFoundContent={isFetching ? <Spinner /> : (term.length < 2 ? 'Type at least 2 characters' : 'No matches')}
              options={options}
            />
          </Form.Item>
          <Alert type="warning" title="This can't be auto-undone. The duplicate is soft-deleted; its references and (for retailers) its outstanding balance move to the survivor." />
        </Form>
      )}
    </Modal>
  );
}
